import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Flex, Text, Icon, Avatar, Tag, iconWeight } from '@nula/core-ui';
import { SidePanel } from '@/components/side-panel/side-panel';
import { CalendarLinkedNote } from './calendar-linked-note';
import { useCalendarStore } from '@/stores/use-calendar-store';
import type { CalendarEvent } from '@/stores/use-calendar-store';
import { getCalendarColor } from '@/lib/calendar/calendar-colors';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { openExternalUrl } from '@/utils/open-external-url';
import styles from './calendar-event-panel.module.css';

const eventPanel = cva(styles.colorDotRow);

interface CalendarEventPanelProps {
  event: CalendarEvent;
  onClose: () => void;
}

export function CalendarEventPanel({ event, onClose }: CalendarEventPanelProps) {
  const { linkDocument, unlinkDocumentEverywhere, subscriptions } = useCalendarStore();
  const service = useWorkspaceService();
  const [isLinking, setIsLinking] = React.useState(false);

  const startTime = new Date(event.startAt);
  const endTime = new Date(event.endAt);
  const timeLabel = event.isAllDay
    ? 'All day'
    : `${fmt12(startTime)} – ${fmt12(endTime)}`;

  const handleCreateDocument = async () => {
    setIsLinking(true);
    try {
      const { id } = await service.createDoc({
        kind: 'document',
        title: event.title,
      });
      await linkDocument(event.id, id, null);
    } finally {
      setIsLinking(false);
    }
  };

  const recurrenceLabel = event.recurrenceRule?.includes('WEEKLY') ? 'Weekly'
    : event.recurrenceRule?.includes('DAILY') ? 'Daily'
      : event.recurrenceRule?.includes('MONTHLY') ? 'Monthly'
        : null;

  const calColor = getCalendarColor(event.subscriptionId, subscriptions[event.subscriptionId]?.color);
  const calColorVar = { '--cal-color': calColor } as React.CSSProperties;

  return (
    <SidePanel title="Event Details" icon="event" onClose={onClose}>
      {/* Color dot + title */}
      <Flex align="start" gap="md" className={eventPanel()}>
        <Flex className={styles.colorDot} style={calColorVar} />
        <Text className={styles.eventTitle}>
          {event.title}
        </Text>
      </Flex>

      {/* Meta row — horizontal wrap like prototype */}
      <Flex
        wrap
        align="center"
        gap="lg"
        className={styles.metaRow}
        style={calColorVar}
      >
        <Flex align="center" gap="sm">
          <Icon name="schedule" size={16} weight={iconWeight.light} className={styles.timeIcon} />
          <Text size="sm" className={styles.timeLabel}>{timeLabel}</Text>
        </Flex>
        {event.meetLink && (
          <Flex
            align="center"
            gap="sm"
            onClick={() => { if (event.meetLink) void openExternalUrl(event.meetLink); }}
            className={styles.meetLink}
            data-meet={event.meetLink.includes('meet.google') ? 'google' : event.meetLink.includes('teams') ? 'teams' : 'generic'}
          >
            <Icon name="videocam" size={14} weight={iconWeight.light} className={styles.meetLinkIcon} />
            <Text size="sm" className={styles.meetLinkLabel}>
              Join {event.meetLink.includes('meet.google') ? 'Meet'
                : event.meetLink.includes('teams') ? 'Teams'
                  : 'call'}
            </Text>
          </Flex>
        )}
        {recurrenceLabel && (
          <Flex align="center" gap="sm">
            <Icon name="repeat" size={16} weight={iconWeight.light} className={styles.metaIcon} />
            <Text size="sm" className={styles.metaLabel}>{recurrenceLabel}</Text>
          </Flex>
        )}
        {event.location && (
          <Flex align="center" gap="sm">
            <Icon name="location_on" size={16} weight={iconWeight.light} className={styles.metaIcon} />
            <Text size="sm" className={styles.metaLabel}>{event.location}</Text>
          </Flex>
        )}
      </Flex>

      {/* Linked note */}
      <Flex vertical gap="md" className={styles.section}>
        <Text className={styles.sectionLabel}>
          Linked Documents
        </Text>

        {event.linkedDocumentIds.length > 0 ? (
          <Flex vertical gap="sm">
            {event.linkedDocumentIds.map(entityId => (
              <CalendarLinkedNote
                key={entityId}
                documentId={entityId}
                color={calColor}
                onRemove={() => { void unlinkDocumentEverywhere(event.id, entityId); }}
              />
            ))}

            {/* Create another */}
            <Flex
              align="center"
              justify="center"
              gap="sm"
              onClick={handleCreateDocument}
              className={styles.createAnotherBtn}
            >
              <Icon name="add" size={16} weight={iconWeight.light} />
              Create another note for this event
            </Flex>
          </Flex>
        ) : (
          /* Empty state — big centered CTA matching prototype */
          <Flex
            vertical
            align="center"
            justify="center"
            gap="md"
            onClick={handleCreateDocument}
            className={clsx(styles.emptyCta, isLinking && styles.linking)}
          >
            <Flex
              align="center"
              justify="center"
              className={styles.emptyCtaIconWrap}
            >
              <Icon name="add" size={20} weight={iconWeight.light} />
            </Flex>
            <Text size="sm" className={styles.emptyCtaLabel}>
              {isLinking ? 'Creating...' : 'Create a document for this event'}
            </Text>
          </Flex>
        )}
      </Flex>

      {/* Attendees */}
      {(event.attendees && event.attendees.length > 0 || event.organizerEmail) && (
        <Flex vertical gap="md" className={styles.section}>
          <Text className={styles.sectionLabel}>
            Attendees
          </Text>
          <Flex wrap gap="sm">
            {/* Organizer chip — shown first with a subtle accent border */}
            {event.organizerEmail && (
              <Tag
                color="accent"
                icon={<Avatar size="sm">{event.organizerName?.[0] ?? event.organizerEmail[0] ?? '?'}</Avatar>}
              >
                {event.organizerName ?? event.organizerEmail}
                <Icon name="workspace_premium" size={12} weight={iconWeight.bold} className={styles.organizerIcon} />
              </Tag>
            )}
            {/* Regular attendees — skip anyone who is the organizer (case-insensitive) */}
            {event.attendees?.filter(att => att.email?.toLowerCase() !== event.organizerEmail?.toLowerCase()).map((att, i) => (
              <Tag
                key={i}
                icon={<Avatar size="sm">{att.name?.[0] ?? att.email?.[0] ?? '?'}</Avatar>}
              >
                {att.name ?? att.email ?? 'Unknown'}
              </Tag>
            ))}
          </Flex>
        </Flex>
      )}

      {/* External link — only shown when the event has a URL */}
      {event.htmlLink && (
        <Flex
          align="center"
          gap="sm"
          role="link"
          onClick={() => { if (event.htmlLink) void openExternalUrl(event.htmlLink); }}
          className={styles.htmlLinkRow}
        >
          <Icon name="open_in_new" size={16} weight={iconWeight.light} className={styles.htmlLinkIcon} />
          <Text size="sm" className={styles.htmlLinkText}>
            Open in External Calendar
          </Text>
        </Flex>
      )}
    </SidePanel>
  );
}

function fmt12(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}
